import { cls } from "@/utils/util";
import TabItem from "./TabItem";
import SubTabItem from "./SubTabItem";
import { useState } from "react";
import useHeaderTabs from "@/hooks/useHeaderTabs";
import { isMarketOnlyUser } from "@/utils/marketUtil";
import { useRecoilValue } from "recoil";
import { useLocation } from "react-router-dom";
import { headerColorState, tokenState } from "@/state";
import HeaderSubTab from "@/component/header/HeaderSubTab";

const Tabs = ({ scrolled }: { scrolled: boolean }) => {
  const token = useRecoilValue(tokenState);
  const location = useLocation();
  const headerColor = useRecoilValue(headerColorState);
  const user = token && token.user_type;
  const tabs = useHeaderTabs();
  const isHome = location.pathname === "/";

  const [isOpenSubTab, setIsOpenSubTab] = useState(false);
  const [hoveredTabUrl, setHoveredTabUrl] = useState("");

  const handleTabHover = (url: string) => {
    setHoveredTabUrl(url);
    setIsOpenSubTab(true);
  };

  const filteredTabs = tabs.filter(({ value }) => !isMarketOnlyUser(user) || value === "market");

  const isTabActive = (tab: { value: string }) => {
    // 예외케이스
    if (tab.value === "qna" && location.pathname.includes("forum")) return true;
    if (tab.value === "class" && location.pathname.includes("edu")) return true;
    if (tab.value === "management" && location.pathname.includes("seminar")) return false;
    // /management/market 은 중고장터다. 경로를 부분 문자열로 보기 때문에 마켓 탭이 먼저 잡혀
    // 개원/경영 서브탭이 사라진다. 마켓 탭은 외부 주소라 내부 경로와 겹칠 일이 없다
    if (tab.value === "market" && location.pathname.startsWith("/management")) return false;
    if (
      tab.value === "post" &&
      (location.pathname.includes("mypost") || location.pathname.includes("chicruiting"))
    )
      return false;
    return location.pathname.includes(tab.value);
  };

  const currentTab = tabs.find((tab) => isTabActive(tab));
  const isSubTabViewed = currentTab && currentTab.subTabs.length > 0;
  const useWideSubTab = isSubTabViewed && (currentTab?.subTabs?.length ?? 0) > 10;

  const renderSubTabs = (ulClassName: string) =>
    currentTab!.subTabs.map((subTab, subIdx) => (
      <ul className={ulClassName} key={`${currentTab!.value}-${subIdx}`}>
        <SubTabItem
          pathname={location.pathname}
          tab={subTab}
          color={scrolled ? null : headerColor}
        />
      </ul>
    ));

  return (
    <div className={cls("border-solid border-gray-200 border-t-[1px] border-opacity-70")}>
      <nav
        className={cls(
          scrolled || isHome ? "bg-white" : "",
          "border-solid border-gray-200 border-opacity-70 scroll-none border-b-[1px]",
        )}
        onMouseLeave={() => setIsOpenSubTab(false)}
      >
        <div
          className={cls(
            "max-w-[1200px] w-full mx-auto px-4 xl:px-0 flex",
            filteredTabs.length > 2 ? "justify-around" : "justify-start",
          )}
        >
          {filteredTabs?.map((tab) => {
            if (!tab) return null;
            const isActive = isTabActive(tab);
            return (
              <ul id={tab.value} className={cls("relative flex items-center")} key={tab.label}>
                <TabItem
                  isActive={isActive}
                  width={tab.width}
                  tab={tab}
                  color={scrolled ? null : headerColor}
                  scrolled={scrolled}
                  onHover={() => {
                    if (!isActive) handleTabHover(tab.url);
                  }}
                ></TabItem>
              </ul>
            );
          })}
        </div>
      </nav>
      {!isMarketOnlyUser(user) &&
        isOpenSubTab &&
        location.pathname !== hoveredTabUrl &&
        currentTab?.value !== "chicruiting" && (
          <HeaderSubTab tabs={tabs} setIsOpenSubTab={setIsOpenSubTab} />
        )}
      {isSubTabViewed && !scrolled && currentTab && (
        <div
          className={cls(
            "max-w-[1200px] w-full mx-auto px-4 xl:px-0",
            useWideSubTab && "overflow-hidden",
          )}
        >
          {useWideSubTab ? (
            <nav className="custom-scrollbar flex justify-start items-center text-sm text-gray-600 min-h-[40px] overflow-x-auto overflow-y-hidden flex-nowrap scroll-smooth">
              {renderSubTabs(
                "relative flex flex-shrink-0 items-center whitespace-nowrap h-[40px] px-4",
              )}
            </nav>
          ) : (
            <div className="flex justify-around text-sm">
              {filteredTabs.map((tab) => (
                <div key={tab.value} style={{ width: tab.width }} className="relative h-[40px]">
                  {currentTab.value === tab.value && (
                    <nav className="absolute w-0 z-10 top-0 flex text-gray-600">
                      {renderSubTabs("relative px-4 first:pl-0 whitespace-nowrap")}
                    </nav>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Tabs;
